import type {
  ReservationOrderForm,
  ReservationOrderJournalEntry,
  ReservationOrderProduct,
  ReservationOrderRow,
  ReservationOrderStatus,
  ReservationQuota
} from 'compob2b-libs';
import {formatRub, machineTitle} from '../portal-catalog/catalog-products.util';
import type {JournalRow} from '../shared/ui/journal.component';
import type {StatusTone} from '../shared/ui/status-appearance.util';

/** Алиас крошки карточки ордера: маршрут — `cabinet.routes.ts`, подпись «Ордер №N» ставит карточка. */
export const ORDER_CARD_CRUMB = 'reservationOrder';

/** Статусы по решению 12. */
export const ORDER_STATUS_LABELS: Record<ReservationOrderStatus, string> = {
  ACTIVE: $localize`:@@OrderStatusActive:Активен`,
  CONVERTED: $localize`:@@OrderStatusConverted:Преобразована в сделку`,
  CANCELLED: $localize`:@@OrderStatusCancelled:Отменена`,
};

/**
 * Цвета `badge()` прототипа: «Актив…» — зелёный, «Отмен…» — красный; «Преобразована в сделку»
 * не попадает ни в один тон прототипа — синий.
 */
export const ORDER_STATUS_TONES: Record<ReservationOrderStatus, StatusTone> = {
  ACTIVE: 'green',
  CONVERTED: 'blue',
  CANCELLED: 'red',
};

export const ORDER_STATUSES: ReservationOrderStatus[] = ['ACTIVE', 'CONVERTED', 'CANCELLED'];

/** ИНН конечного клиента — 10 или 12 цифр (прототип `evaluate()`). */
export function isInn(value: string | null | undefined): boolean {
  return /^\d{10}(\d{2})?$/.test((value ?? '').trim());
}

/** «18.10.2026» из ISO-даты или даты-времени; пусто — пустая строка. */
export function formatDay(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return match ? `${match[3]}.${match[2]}.${match[1]}` : '';
}

/** «XCMG XE215C» — модель как в прототипе. */
export function orderModelTitle(product: Pick<ReservationOrderProduct, 'header'> | null | undefined): string {
  return machineTitle(product?.header);
}

/** Цена ордера — снимок при создании (Э8-11); нет — `null`, шаблон пишет «Цена по запросу». */
export function orderPrice(price: number | null | undefined): string | null {
  return formatRub(price);
}

/** Свободно в квоте; лимит 0 или превышение не уходят в минус. */
export function quotaFree(quota: Pick<ReservationQuota, 'active' | 'limit'>): number {
  return Math.max(0, quota.limit - quota.active);
}

/** Заполнение прогресса квоты, %. */
export function quotaPercent(quota: Pick<ReservationQuota, 'active' | 'limit'>): number {
  return Math.min(100, quota.active / Math.max(1, quota.limit) * 100);
}

/** Поля мастера, из которых собирается запрос `check` / `create`. */
export interface OrderDraft {
  clientId: number | null;
  /** Клиент вне скоупа, найденный по точному ИНН (Э8-17). */
  inn: string | null;
  productUid: string | null;
  counterPartyId: number | null;
  authorCustomerId: number | null;
  clientContact: string;
  comment: string;
}

/** Запрос к бэку: клиент — `clientId` или `inn`; владелец и автор — только выбранные; пустые тексты — `null`. */
export function buildOrderForm(draft: OrderDraft): ReservationOrderForm {
  const form: ReservationOrderForm = {productUid: draft.productUid ?? ''};
  if (draft.clientId) {
    form.clientId = draft.clientId;
  } else if (draft.inn) {
    form.inn = draft.inn.trim();
  }
  if (draft.counterPartyId) {
    form.counterPartyId = draft.counterPartyId;
  }
  if (draft.authorCustomerId) {
    form.authorCustomerId = draft.authorCustomerId;
  }
  form.clientContact = draft.clientContact.trim() || null;
  form.comment = draft.comment.trim() || null;
  return form;
}

/** Строка CSV — значения колонок в порядке таблицы. */
export function orderCsvRow(row: ReservationOrderRow): string[] {
  return [
    `№${row.number} ${formatDay(row.createdAt)}`,
    `${row.client.company} ${row.client.inn}`,
    row.owner.header,
    [orderModelTitle(row.product), row.product.categoryHeader].filter(Boolean).join(' '),
    orderPrice(row.price) ?? '',
    ORDER_STATUS_LABELS[row.status] ?? row.status,
    formatDay(row.expiresAt),
  ];
}

export const ORDER_LOG_ACTION_LABELS: Record<string, string> = {
  CREATED: $localize`:@@OrderLogCreated:Ордер создан`,
  CANCELLED: $localize`:@@OrderLogCancelled:Ордер отменён`,
  EXPIRED: $localize`:@@OrderLogExpired:Отменена · истёк срок`,
  EXTENDED: $localize`:@@OrderLogExtended:Срок продлён`,
  CONVERTED: $localize`:@@OrderLogConverted:Преобразован в сделку`,
  COMMENTED: $localize`:@@OrderLogCommented:Комментарий изменён`,
  ATTACHMENT_ADDED: $localize`:@@OrderLogFileAdded:Файл добавлен`,
  ATTACHMENT_REMOVED: $localize`:@@OrderLogFileRemoved:Файл удалён`,
};

/** Поля снимка, которые показываем в журнале, в этом порядке; служебные id не показываем. */
const ORDER_LOG_FIELDS: Record<string, string> = {
  status: $localize`:@@Status:Статус`,
  client: $localize`:@@OrderEndClient:Конечный клиент`,
  counterParty: $localize`:@@OrderColumnDealer:Дилер`,
  author: $localize`:@@OrderManager:Менеджер`,
  product: $localize`:@@ModelDetailModel:Модель`,
  category: $localize`:@@ModelDetailType:Вид техники`,
  price: $localize`:@@OrderColumnPrice:Стоимость`,
  expiresAt: $localize`:@@OrderColumnExpires:Действует до`,
  comment: $localize`:@@OrderComment:Комментарий`,
  fileName: $localize`:@@OrderLogFile:Файл`,
};

function logValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (field === 'status') {
    return ORDER_STATUS_LABELS[value as ReservationOrderStatus] ?? String(value);
  }
  if (field === 'expiresAt') {
    return formatDay(String(value));
  }
  if (field === 'price') {
    return orderPrice(Number(value)) ?? '—';
  }
  return String(value);
}

/**
 * Запись журнала → строка кирпича `journal`: подпись действия и изменённые поля. Создание —
 * «Поле: значение», изменение — «Поле: было → стало» только по отличающимся полям.
 */
export function toOrderJournalRow(entry: ReservationOrderJournalEntry): JournalRow {
  const before = entry.before ?? {};
  const after = entry.after ?? {};
  const changes = Object.keys(ORDER_LOG_FIELDS)
    .filter(field => field in before || field in after)
    .filter(field => !entry.before || !entry.after || before[field] !== after[field])
    .map(field => {
      const label = ORDER_LOG_FIELDS[field];
      if (!entry.before) {
        return `${label}: ${logValue(field, after[field])}`;
      }
      if (!entry.after) {
        return `${label}: ${logValue(field, before[field])}`;
      }
      return `${label}: ${logValue(field, before[field])} → ${logValue(field, after[field])}`;
    });
  return {at: entry.createdAt, author: entry.actorName, action: ORDER_LOG_ACTION_LABELS[entry.action] ?? entry.action, changes};
}
